// The LOADOUT sheet of a hull in the pens (R2017, R2031; ui-map-interface.md
// §12 "FLEET · MISSIONS").
//
// The load mix of a docked hull is one picker card of the kit, beside the
// captain picker (R1893): the context slot of a desktop, a page of the menu
// sheet on a telephone. LOADOUT in ⋯ of the row opens it, for a hull that
// loads two or more round kinds.
//
//   LOADOUT · Lancer 'Kestrel'       9/12
//   HARPOON  ‹ 6 › »  held 24
//   REMORA   ‹ 3 › »  held  8
//   TORPOR   ‹ 0 › »  held  4
//   [ AUTO ]               [ STOW ]
//
// One row for each kind the hull can load: the name, a stepper of the
// choice kit, the MAX press » and the stock of the hold. The + of a row
// stands disabled when one more round of the kind does not fit the
// magazine (`canAdd`), and the - at 0. MAX fills the free room of the
// magazine with the kind of its row and stands disabled with the +
// (R2040). AUTO sends an empty mix; STOW sends the draft (R2703). The
// server holds the rule (`game.MixFits`) and refuses a mix that does not
// fit with the notice `load_does_not_fit`.
//
// The module is pure: it reads the state and the draft and writes the
// card spec.

import { overlay as S } from '../../gen/strings';
import type { CardSpec } from '../cards/card';
import { shipMarkSvg } from '../cards/marks';
import { esc } from '../esc';
import type { SceneLook } from '../../render/scene-look';
import type { GameState } from '../../state';
import { roundsHeld } from './infocards';
import {
  canAdd, countOf, loadableKinds, magazineRounds, mixCargo, mixLine, totalOf, type Stock,
} from '../rounds';
import { copyStock, maxStock, stepStock, stockActions, stockRow } from './stockpicker';

/** The kinds a penned hull can load, in the fixed order. */
function penKinds(s: GameState, cls: number): number[] {
  return loadableKinds(s.ship(cls), s.catalog?.torpedoes ?? []);
}

/** The draft a LOADOUT sheet opens with: the saved mix of the hull, or none
 *  while it loads AUTO. */
export function draftOf(s: GameState, fleetId: number): Stock[] {
  const st = s.pennedHull(fleetId);
  return st ? copyStock(st.loadMix) : [];
}

/**
 * One step of a stepper: the draft with one round of `kind` more (+1) or
 * less (-1), clamped at 0 and at the magazine. A step that would break the
 * clamp returns the draft as it was.
 */
export function stepDraft(s: GameState, fleetId: number, draft: readonly Stock[],
  kind: number, dir: number): Stock[] {
  const st = s.pennedHull(fleetId);
  if (!st) return [...draft];
  return stepStock(draft, kind, dir, penKinds(s, st.cls),
    dir < 0 || canAdd(draft, kind, st.magazineCargo, s.catalog?.torpedoes ?? []));
}

/**
 * The MAX press of a row (R2040): the draft with the free room of the
 * magazine filled with `kind`. The other rows keep their counts, and the
 * stock of the hold does not limit it, as it does not limit the +.
 */
export function maxDraft(s: GameState, fleetId: number, draft: readonly Stock[],
  kind: number): Stock[] {
  const st = s.pennedHull(fleetId);
  const torps = s.catalog?.torpedoes ?? [];
  if (!st) return [...draft];
  return maxStock(draft, kind, penKinds(s, st.cls), st.magazineCargo - mixCargo(draft, torps),
    torps.find((t) => t.id === kind)?.cargo ?? 0);
}

/**
 * The LOADOUT sheet of one hull in the pens, or null when the hull left the
 * pens. The status chip states the draft against the magazine; the kind
 * line names the class and the saved load. A row of a kind the hold holds
 * fewer of than the draft asks for raises the note that the hull sails
 * with what the hold has (the server's `load_short`).
 */
export function loadSheetSpec(s: GameState, fleetId: number, draft: readonly Stock[],
  look?: SceneLook): CardSpec | null {
  const st = s.pennedHull(fleetId);
  if (!st) return null;
  const ship = s.ship(st.cls);
  const torps = s.catalog?.torpedoes ?? [];
  const kinds = penKinds(s, st.cls);
  const nameOf = (k: number) => s.torpedo(k)?.name ?? '?';
  const room = magazineRounds(st.magazineCargo, kinds, torps);
  const total = totalOf(draft);
  const saved = mixLine(st.loadMix, nameOf) || S.word_auto;
  let short = false;
  const rows = kinds.map((k) => {
    const n = countOf(draft, k);
    const held = roundsHeld(s, k);
    if (n > held) short = true;
    const name = nameOf(k).toUpperCase();
    const full = !canAdd(draft, k, st.magazineCargo, torps);
    return stockRow({
      kind: k, name, count: n, full, stepAct: 'loadstep', maxAct: 'loadmax',
      less: S.load_less({ name }), more: S.load_more({ name }), max: S.load_max({ name }), held: S.load_held({ n: held }),
    });
  }).join('');
  const lines = total === 0 ? [esc(S.load_note_auto)] : short ? [esc(S.load_note_short)] : [];
  return {
    size: 'card', mark: shipMarkSvg(st.cls, look), title: S.load_title({ hull: st.name }),
    ink: { faction: s.faction },
    kind: S.load_kind({ cls: (ship?.name ?? '?').toUpperCase(), now: saved }),
    status: { text: room > 0 ? `${total}/${room}` : String(total) },
    close: true, closeAct: 'pickclose',
    body: `<div class="ck-load">${rows}</div>`,
    note: lines.length > 0 ? { lines } : undefined,
    actions: stockActions('load', fleetId, draft, st.loadMix),
  };
}
